import Vue from 'vue';
import { Model } from '@viddsee/server-api-client';

import Loader from '@/scripts/Components/Molecules/VideoLoadingSpinner.vue';
import CardRow from '@/scripts/Components/Row/CardRow.vue';

import WaypointHandler from '@/scripts/Components/libs/waypoint-handler';
import { Nullable } from '@/scripts/models/utility-types';
import { browseApi } from '@/scripts/webcall/v3/browse';

export interface BrowseLikedComponentData {
  videos: Model.Video.Info[];
  isLoading: boolean;
  hasErrorLoading: boolean;
  loadedPage: number;
  hasMorePages: boolean;
  waypoint: Nullable<WaypointHandler>;

  $refs: {
    'video-grids'?: HTMLElement;
  };

  loadVideos(): Promise<VideoResponseData>;
  processLoadedVideo(data: VideoResponseData): VideoResponseData;
  updateApplicationPreState(): void;
  updateApplicationPostState(data: VideoResponseData): VideoResponseData;
  processErroneousRequest(): void;
  loadMoreVideos(): void;
  setUpWaypoint(): void;
}

type VideoResponseData = Model.Pagination.PaginatedItemsResponse<Model.Video.Info>;

const BrowseLikedComponentMixin = {
  components: {
    CardRow,
    Loader,
  },
  methods: {
    async loadVideos(this: BrowseLikedComponentData): Promise<VideoResponseData> {
      // By default load the popular page with default parameters
      const { data } = await browseApi.listPopular();
      return data;
    },

    processLoadedVideo(this: BrowseLikedComponentData, data: VideoResponseData): VideoResponseData {
      this.videos.push(...data.items);
      return data;
    },

    updateApplicationPreState(this: BrowseLikedComponentData): void {
      this.isLoading = true;
      this.hasErrorLoading = false;
    },

    updateApplicationPostState(this: BrowseLikedComponentData, data: VideoResponseData): VideoResponseData {
      this.loadedPage += 1;
      this.isLoading = false;
      this.hasErrorLoading = false;
      this.hasMorePages = this.loadedPage < data.pageCount;
      this.waypoint && this.waypoint.update();
      return data;
    },

    processErroneousRequest(this: BrowseLikedComponentData): void {
      this.waypoint && this.waypoint.destroy();
      this.isLoading = false;
      this.hasErrorLoading = true;
    },

    loadMoreVideos(this: BrowseLikedComponentData): void {
      if (this.isLoading) return;

      this.updateApplicationPreState();
      this.loadVideos()
        .then(this.processLoadedVideo)
        .then(this.updateApplicationPostState)
        .catch(this.processErroneousRequest)
      ;
    },

    // Utility methods
    setUpWaypoint(this: BrowseLikedComponentData): void {
      this.waypoint = new WaypointHandler(
        {
          element: this.$refs['video-grids'],
          handler: this.loadMoreVideos,
          offset: 'bottom-in-view',
        },
        () => this.hasMorePages,
      );
    },
  },
};

export default BrowseLikedComponentMixin;

export const BrowseLikedComponent = Vue.extend(BrowseLikedComponentMixin);
